import React from 'react';
import { Link } from 'react-router-dom';
import { CopilotActionProposed } from '../../models/copilot.model';
import { StateLanguage } from '../../languages/config/StateLanguage';

interface Props {
  action: CopilotActionProposed;
  onConfirm: (actionId: string) => void;
  onReject: (actionId: string) => void;
  readOnly?: boolean;
  resultId?: string;
  resultSuccess?: boolean;
}

type Labels = Record<string, string>

function safeStr(val: any): string {
  if (val === null || val === undefined || val === '') return ''
  if (typeof val === 'string') return val
  if (typeof val === 'number' || typeof val === 'boolean') return String(val)
  return ''
}

function displayVal(val: any, labels: Labels): string {
  const s = safeStr(val)
  if (s && labels[s]) return labels[s]
  return s
}

const HIDDEN_KEYS = new Set(['labels', 'items'])

function fieldLabel(key: string, toolName: string, langLabels: any): string {
  const map: Record<string, string | ((t: string) => string)> = {
    'id_contact': (t: string) => {
      if (t.includes('provider') || t === 'create_expense') return langLabels.provider ?? 'Fournisseur'
      return langLabels.customer ?? langLabels.contact ?? 'Contact'
    },
    'treasury_account_id': langLabels.treasuryAccount ?? 'Compte de tresorerie',
    'id_account': langLabels.account ?? 'Compte',
    'id_journal': langLabels.journal ?? 'Journal',
    'document_date': langLabels.date ?? 'Date',
    'concerning': langLabels.description ?? 'Description',
    'invoice_number': langLabels.reference ?? 'Reference',
    'reference': langLabels.reference ?? 'Reference',
    'currency': langLabels.currency ?? 'Devise',
  }
  const entry = map[key]
  if (typeof entry === 'function') return entry(toolName)
  if (typeof entry === 'string') return entry
  // Fallback: humanize key
  return key.replace(/^id_/, '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function formatDate(val: any): string {
  const s = safeStr(val)
  if (!s) return ''
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const [y, m, d] = s.substring(0, 10).split('-')
    return `${d}.${m}.${y}`
  }
  return s
}

function formatAddress(addr: any): string {
  if (!addr || typeof addr !== 'object') return ''
  const parts = [
    [safeStr(addr.street), safeStr(addr.number)].filter(Boolean).join(' '),
    safeStr(addr.additional),
    [safeStr(addr.zip_code), safeStr(addr.city)].filter(Boolean).join(' '),
    safeStr(addr.country),
  ].filter(Boolean)
  return parts.join(', ')
}

const Field: React.FC<{label: string; value: any}> = ({label, value}) => {
  const v = safeStr(value)
  if (!v) return null
  return (
    <div className="d-flex fs-8 mb-1">
      <span className="text-muted me-2" style={{minWidth: '80px'}}>{label}:</span>
      <span className="text-gray-800 fw-bold">{v}</span>
    </div>
  )
}

function renderContactPreview(p: any, lang: any) {
  const addr = formatAddress(p.address)
  const iban = safeStr(p.bank_account?.iban)
  const currency = safeStr(p.currency?.code || p.currency)
  return (
    <>
      <Field label={lang.labels.name} value={safeStr(p.name) || [safeStr(p.first_name), safeStr(p.last_name)].filter(Boolean).join(' ')} />
      <Field label={lang.labels.type} value={p.type} />
      {addr && <Field label={lang.labels.address} value={addr} />}
      <Field label={lang.labels.email} value={p.email} />
      <Field label={lang.labels.phone} value={p.phone} />
      <Field label={lang.labels.vatNumber} value={p.vat_number} />
      {iban && <Field label="IBAN" value={iban} />}
      {currency && <Field label={lang.labels.currency} value={currency} />}
    </>
  )
}

function formatTaxRate(val: any): string {
  if (val === null || val === undefined || val === '') return ''
  const n = Number(val)
  if (isNaN(n)) return ''
  // If value looks like a decimal ratio (< 1), convert to percentage
  const pct = n < 1 && n > 0 ? n * 100 : n
  return `${parseFloat(pct.toFixed(2))} %`
}

function formatPrice(val: any): string {
  if (val === null || val === undefined || val === '') return ''
  const n = Number(val)
  if (isNaN(n)) return ''
  return n.toFixed(2)
}

function formatQty(item: any): string {
  const q = safeStr(item.quantity)
  if (!q) return ''
  const unit = safeStr(item.unit?.symbol || item.unit?.name || item.unit)
  return unit ? `${q} ${unit}` : q
}

function renderItemsTable(items: any[], lang: any, currencySymbol: string, labels: Labels) {
  if (items.length === 0) return null
  return (
    <div className="mt-2">
      <table className="table table-sm table-bordered fs-9 mb-0">
        <thead>
          <tr className="text-muted text-uppercase">
            <th>{lang.labels.description}</th>
            <th className="text-end" style={{width: '60px'}}>{lang.labels.quantity}</th>
            <th className="text-end" style={{width: '80px'}}>{lang.labels.price}</th>
            <th className="text-end" style={{width: '60px'}}>{lang.labels.tax}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item: any, i: number) => {
            const price = formatPrice(item.price_unit ?? item.unit_price)
            const desc = displayVal(item.detail || item.description || item.name, labels)
            return (
              <tr key={i}>
                <td>{desc}</td>
                <td className="text-end">{formatQty(item)}</td>
                <td className="text-end">{price ? `${price}${currencySymbol ? ' ' + currencySymbol : ''}` : ''}</td>
                <td className="text-end">{formatTaxRate(item.tax_rate)}</td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function renderInvoicePreview(p: any, lang: any, labels: Labels, toolName: string) {
  const items: any[] = Array.isArray(p.items) ? p.items : []
  const currencyCode = safeStr(p.currency?.code || p.currency)
  const currencySymbol = safeStr(p.currency?.symbol) || currencyCode
  const contact = displayVal(p.id_contact, labels)
  return (
    <>
      {contact && <Field label={fieldLabel('id_contact', toolName, lang.labels)} value={contact} />}
      <Field label={lang.labels.reference} value={p.invoice_number || p.reference} />
      <Field label={lang.labels.date} value={formatDate(p.document_date || p.date)} />
      <Field label={lang.labels.description} value={p.description || p.concerning} />
      {currencyCode && <Field label={lang.labels.currency} value={currencyCode} />}
      {displayVal(p.treasury_account_id, labels) && <Field label={fieldLabel('treasury_account_id', toolName, lang.labels)} value={displayVal(p.treasury_account_id, labels)} />}
      {renderItemsTable(items, lang, currencySymbol, labels)}
    </>
  )
}

function renderGenericPreview(p: any, lang: any, labels: Labels, toolName: string) {
  if (!p || typeof p !== 'object') {
    const s = safeStr(p)
    return s ? <span className="fs-8 text-gray-700">{s}</span> : null
  }
  const entries = Object.entries(p).filter(([key, v]) => {
    if (HIDDEN_KEYS.has(key)) return false
    if (v === null || v === undefined || v === '') return false
    if (Array.isArray(v) && v.length === 0) return false
    if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0) return false
    return true
  })
  const items: any[] = Array.isArray(p.items) ? p.items : []
  if (entries.length === 0 && items.length === 0) return null
  const currencySymbol = safeStr(p.currency?.symbol || p.currency?.code || p.currency)
  return (
    <>
      {entries.map(([key, val]) => {
        if (typeof val === 'object' && !Array.isArray(val)) {
          const nested = Object.entries(val as Record<string, any>).filter(([, v]) => safeStr(v)).map(([k, v]) => `${k}: ${safeStr(v)}`).join(', ')
          return nested ? <Field key={key} label={fieldLabel(key, toolName, lang.labels)} value={nested} /> : null
        }
        if (Array.isArray(val)) {
          return <Field key={key} label={fieldLabel(key, toolName, lang.labels)} value={`${val.length} items`} />
        }
        return <Field key={key} label={fieldLabel(key, toolName, lang.labels)} value={displayVal(val, labels)} />
      })}
      {items.length > 0 && renderItemsTable(items, lang, currencySymbol, labels)}
    </>
  )
}

const routeMap: Record<string, string> = {
  'create_contact': '/main/contact/view/',
  'create_quote': '/main/sale/quote/view/',
  'create_order': '/main/sale/order/view/',
  'create_invoice': '/main/sale/invoice/view/',
  'create_provider_order': '/main/purchase/order/view/',
  'create_provider_invoice': '/main/purchase/invoice/view/',
  'create_expense': '/main/purchase/expense/view/',
  'create_accounting_entry': '/main/accounting/entry/view/',
}

const CopilotActionCard: React.FC<Props> = ({action, onConfirm, onReject, readOnly, resultId, resultSuccess}) => {
  const {lang} = StateLanguage()
  const {actionId, toolName, preview, status: actionStatus} = action
  const busy = actionStatus === 'confirming' || actionStatus === 'rejecting'
  const labels: Labels = (preview && typeof preview === 'object' && preview.labels && typeof preview.labels === 'object') ? preview.labels : {}

  const titleMap: Record<string, string> = {
    'create_contact': lang.labels.copilotActionCreateContact,
    'create_invoice': lang.labels.copilotActionCreateInvoice,
    'create_quote': lang.labels.copilotActionCreateQuote,
    'create_order': lang.labels.copilotActionCreateOrder,
    'create_provider_invoice': lang.labels.copilotActionCreateProviderInvoice,
    'create_provider_order': lang.labels.copilotActionCreateProviderOrder,
    'create_expense': lang.labels.copilotActionCreateExpense,
    'create_accounting_entry': lang.labels.copilotActionCreateAccountingEntry,
    'start_batch_processing': lang.labels.copilotActionStartBatch,
  }
  const title = titleMap[toolName] || lang.labels.copilotActionProposed

  const invoiceTools = new Set(['create_invoice', 'create_quote', 'create_order', 'create_provider_invoice', 'create_provider_order', 'create_expense'])

  let previewContent: React.ReactNode = null
  if (toolName === 'start_batch_processing') {
    previewContent = <span className="fs-8 text-gray-700">{lang.labels.copilotBatchConfirmText}</span>
  } else if (toolName === 'create_contact') {
    previewContent = renderContactPreview(preview, lang)
  } else if (invoiceTools.has(toolName)) {
    previewContent = renderInvoicePreview(preview, lang, labels, toolName)
  } else {
    previewContent = renderGenericPreview(preview, lang, labels, toolName)
  }

  const viewUrl = readOnly && resultId ? (routeMap[toolName] ? routeMap[toolName] + resultId : null) : null

  return (
    <div className={`rounded border ${readOnly ? (resultSuccess !== false ? 'border-success' : 'border-danger') : 'border-primary'} py-4 px-4 bg-white mw-lg-400px`} style={{width: '100%'}}>
      <div className="fw-bold fs-7 text-gray-900 mb-3">{title}</div>

      {previewContent && (
        <div className="rounded bg-light p-3 mb-3">
          {previewContent}
        </div>
      )}

      {readOnly ? (
        viewUrl && (
          <div className="mt-1">
            <Link to={viewUrl} target="_blank" rel="noopener noreferrer" className="fs-8 fw-bold text-primary text-hover-dark">
              {lang.labels.copilotViewPiece} &rarr;
            </Link>
          </div>
        )
      ) : (
        <div className="d-flex gap-2">
          <button
            className="btn btn-sm btn-primary flex-grow-1"
            type="button"
            disabled={busy}
            onClick={() => onConfirm(actionId)}
          >
            {actionStatus === 'confirming' && <span className="spinner-border spinner-border-sm me-2" />}
            {actionStatus === 'confirming' ? lang.labels.copilotConfirming : lang.labels.copilotConfirm}
          </button>
          <button
            className="btn btn-sm btn-light-danger flex-grow-1"
            type="button"
            disabled={busy}
            onClick={() => onReject(actionId)}
          >
            {actionStatus === 'rejecting' && <span className="spinner-border spinner-border-sm me-2" />}
            {actionStatus === 'rejecting' ? lang.labels.copilotRejecting : lang.labels.copilotReject}
          </button>
        </div>
      )}
    </div>
  )
}

export default CopilotActionCard;
